import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {doesAccountNeedReconnection} from '@Components/social-media/account.vo';
import {useDeleteSocialAccount} from '@Components/social-media/social-media.hooks';
import {getPostableSocialMediaAccountsForUser} from '@Components/social-media/social-media-thunk';
import {useAppDispatch} from '@/hooks';
import type {SocialAccountRowMenuProps} from './social-account-row-menu.types';
import {SOCIAL_ACCOUNT_ROW_MENU_DELETE_ITEM_ID, SOCIAL_ACCOUNT_ROW_MENU_REFRESH_ITEM_ID} from './social-account-row-menu.types';
import styles from './social-account-row-menu.module.scss';

export function SocialAccountRowMenu({account, className = '', onRefreshAccount, onMenuOpened, onMenuClosed}: SocialAccountRowMenuProps): ReactElement {
  const dispatch = useAppDispatch();
  const {deleteAccount} = useDeleteSocialAccount();

  const onDeleteClicked = (): void => {
    openConfirmDeleteModal({
      text: window.i18next.t('pmwjs_social_account_deletion_message_with_platform', {platformName: getSocialPlatformName(account.type)}),
      secondaryCtaText: window.i18next.t('pmwjs_cancel'),
      showSecondaryCta: true,
      onDeleteConfirmation: deleteAccountAndReloadList,
    });
  };

  const deleteAccountAndReloadList = async (): Promise<void> => {
    await deleteAccount(account);
    await dispatch(getPostableSocialMediaAccountsForUser());
  };

  const getMenuSelector = (): ReactElement => {
    return (
      <Icon
        icon="icon-menu-dots"
        size={IconSize.SIZE_ICON_16}
        type={IconType.TRANSPARENT}
        shape={IconShape.SQUARE}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_account_options')}
      />
    );
  };

  const getRefreshMenuItem = (): ReactElement | null => {
    if (doesAccountNeedReconnection(account) || account.isLockedByPlanLimit) {
      return null;
    }

    return (
      <ControlledListItemType7
        id={SOCIAL_ACCOUNT_ROW_MENU_REFRESH_ITEM_ID}
        className={styles.menuListItem}
        text={window.i18next.t('pmwjs_refresh')}
        icon="icon-refresh"
        onClick={onRefreshAccount}
      />
    );
  };

  const getMenuPopup = (): ReactElement => {
    return (
      <ul className={styles.menuList}>
        {getRefreshMenuItem()}
        <ControlledListItemType7
          id={SOCIAL_ACCOUNT_ROW_MENU_DELETE_ITEM_ID}
          className={styles.menuListItem}
          text={window.i18next.t('pmwjs_delete_account')}
          icon="icon-delete"
          iconClasses={styles.deleteIcon}
          onClick={onDeleteClicked}
        />
      </ul>
    );
  };

  return (
    <Dropdown
      className={`${styles.menuDropdown} ${className}`}
      popupClassName={styles.menuPopup}
      modalTitle={window.i18next.t('pmwjs_account_options')}
      selector={getMenuSelector()}
      popup={getMenuPopup()}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      position={DROPDOWN_POSITION.ABSOLUTE}
      selectorClassNameOnOpen="-active"
      shouldCenterAlignModalTitle={false}
      onOpen={onMenuOpened}
      onClose={onMenuClosed}
      stopClickPropagation
      doCloseOnInnerClick
      doesCloseOnOuterClick
      showModalForSmallScreen
    />
  );
}
